import { withErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import {
  ArtifactType,
  AskAiChatMessageFragmentFragment,
} from "types/graphql-schema";

import AskAISuggestedArtifact from "@apps/ask-ai/components/ask-ai-embed-suggested-artifact";
import ErrorFallback from "@components/error-fallback/error-fallback";
import { assertEdgesNonNull } from "@helpers/helpers";
import { sanitizeMarkdown } from "@helpers/string";

import AskAiArtifact from "./ask-ai-artifact";
import AskAIEmbedExternalEvent from "./ask-ai-embed-external-event";
import AskAIEmbedGoalProgressUpdate from "./ask-ai-embed-goal-progress-update";
import AskAIEmbedMeeting from "./ask-ai-embed-meeting";
import AskAIEmbedOverviewDirectReports from "./ask-ai-embed-overview-direct-reports";
import AskAIEmbedOverviewUser from "./ask-ai-embed-overview-user";
import AskAIEmbedSuggestedAssessmentResponses from "./ask-ai-embed-suggested-assessment-responses";
import AskAIEmbedsuggestedTopic from "./ask-ai-embed-suggested-topic";
import {
  askAiEmbedContainerClassName,
  askAiEmbedItemClassName,
} from "./ask-ai-embeds-constants";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";

const AskAIEmbedOutputItemComponent = ({
  outputItem,
  chatSessionId,
  messageId,
  createdArtifact,
  isLastMessage,
}: {
  isLastMessage: boolean;
  messageId: string;
  outputItem: NonNullable<
    NonNullable<AskAiChatMessageFragmentFragment["outputItems"]>[number]
  >;
  chatSessionId: number | null;
  createdArtifact: AskAiChatMessageFragmentFragment["createdArtifact"];
}) => {
  const actionItems = outputItem.actionItems
    ? assertEdgesNonNull(outputItem.actionItems)
    : [];
  const goals = outputItem.goals ? assertEdgesNonNull(outputItem.goals) : [];
  const meetings = outputItem.meetings
    ? assertEdgesNonNull(outputItem.meetings)
    : [];
  const recognitions = outputItem.recognitions
    ? assertEdgesNonNull(outputItem.recognitions)
    : [];
  const feedbacks = outputItem.feedback
    ? assertEdgesNonNull(outputItem.feedback)
    : [];
  const artifacts = compact([
    ...actionItems,
    ...goals,
    ...recognitions,
    ...feedbacks,
  ]);

  const suggestedMeetingTopics = outputItem.suggestedMeetingTopics;
  const externalEvents = compact(outputItem.externalEvents);
  const goalProgressUpdates = outputItem.goalProgressUpdates || [];
  const suggestedAssessmentResponses = (
    outputItem.suggestedAssessmentResponses || []
  ).map((response) => ({
    suggestedResponse: response?.suggestedResponse || "",
    citations: compact(response?.citations || []),
    suggestionId: response?.suggestionId || null,
  }));

  if (!chatSessionId) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {outputItem.text && (
        <div className="prose prose-sm max-w-none">
          <AskAIMessageMarkdown>
            {sanitizeMarkdown(outputItem?.text) || ""}
          </AskAIMessageMarkdown>
        </div>
      )}

      {artifacts.length > 0 && (
        <div className={askAiEmbedContainerClassName}>
          {artifacts.map((artifact) => (
            <div className={askAiEmbedItemClassName} key={artifact.id}>
              <AskAiArtifact id={artifact.id} />
            </div>
          ))}
        </div>
      )}
      {suggestedAssessmentResponses.length > 0 && (
        <AskAIEmbedSuggestedAssessmentResponses
          suggestedAssessmentResponses={suggestedAssessmentResponses}
          canBeEdited={!isLastMessage}
          chatSessionId={chatSessionId}
          messageId={messageId}
        />
      )}
      {outputItem.suggestedFeedback && !createdArtifact && (
        <div className={askAiEmbedContainerClassName}>
          <div className={askAiEmbedItemClassName}>
            <AskAISuggestedArtifact
              chatSessionId={chatSessionId}
              messageId={messageId}
              suggestedArtifact={{
                artifactType: ArtifactType.Feedback,
                ...outputItem.suggestedFeedback,
              }}
            />
          </div>
        </div>
      )}
      {outputItem.suggestedGoal && !createdArtifact && (
        <div className={askAiEmbedContainerClassName}>
          <div className={askAiEmbedItemClassName}>
            <AskAISuggestedArtifact
              chatSessionId={chatSessionId}
              messageId={messageId}
              suggestedArtifact={{
                artifactType: ArtifactType.Goal,
                ...outputItem.suggestedGoal,
              }}
            />
          </div>
        </div>
      )}
      {outputItem.suggestedActionItem && !createdArtifact && (
        <div className={askAiEmbedContainerClassName}>
          <div className={askAiEmbedItemClassName}>
            <AskAISuggestedArtifact
              chatSessionId={chatSessionId}
              messageId={messageId}
              suggestedArtifact={{
                artifactType: ArtifactType.ActionItem,
                ...outputItem.suggestedActionItem,
              }}
            />
          </div>
        </div>
      )}
      {outputItem.suggestedRecognition && !createdArtifact && (
        <div className={askAiEmbedContainerClassName}>
          <div className={askAiEmbedItemClassName}>
            <AskAISuggestedArtifact
              chatSessionId={chatSessionId}
              messageId={messageId}
              suggestedArtifact={{
                artifactType: ArtifactType.Recognition,
                ...outputItem.suggestedRecognition,
              }}
            />
          </div>
        </div>
      )}

      {meetings.length > 0 && (
        <div className={askAiEmbedContainerClassName}>
          {meetings.map((meeting) => (
            <div className={askAiEmbedItemClassName} key={meeting.id}>
              <AskAIEmbedMeeting meeting={meeting} />
            </div>
          ))}
        </div>
      )}
      {suggestedMeetingTopics && (
        <div className={askAiEmbedContainerClassName}>
          <div className={askAiEmbedItemClassName}>
            <AskAIEmbedsuggestedTopic
              meeting={suggestedMeetingTopics.meeting}
              suggestedTopics={compact(suggestedMeetingTopics.topics) || []}
            />
          </div>
        </div>
      )}
      {goalProgressUpdates.length > 0 && (
        <div>
          {goalProgressUpdates.map((goalProgressUpdate) => (
            <div
              className={askAiEmbedContainerClassName}
              key={goalProgressUpdate?.goal?.id}
            >
              <div className={askAiEmbedItemClassName}>
                <AskAIEmbedGoalProgressUpdate
                  goalId={goalProgressUpdate?.goal?.id}
                  suggestedMessage={goalProgressUpdate?.suggestedMessage}
                  showCheckinButton
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {outputItem.overviewDirectReports && (
        <AskAIEmbedOverviewDirectReports outputItem={outputItem} />
      )}
      {outputItem.overviewUser && (
        <AskAIEmbedOverviewUser overviewUser={outputItem.overviewUser} />
      )}
      {externalEvents.length > 0 && (
        <div className={askAiEmbedContainerClassName}>
          {externalEvents.map((event: any) => (
            <div className={askAiEmbedItemClassName} key={event.id}>
              <AskAIEmbedExternalEvent eventData={event.eventData} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AskAIEmbedsComponent = ({
  message,
  chatSessionId,
  isLastMessage,
}: {
  message: AskAiChatMessageFragmentFragment;
  chatSessionId: number | null;
  isLastMessage: boolean;
}) => {
  const outputItems = compact(message.outputItems);

  if (!chatSessionId) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {outputItems.map((outputItem, index) => (
        <AskAIEmbedOutputItemComponent
          key={`${message.id}-${outputItem.type}-${index}`}
          outputItem={outputItem}
          chatSessionId={chatSessionId}
          messageId={message.id}
          createdArtifact={message.createdArtifact}
          isLastMessage={isLastMessage}
        />
      ))}
      {message.createdArtifact && (
        <div className={askAiEmbedContainerClassName}>
          <div className={askAiEmbedItemClassName}>
            <AskAiArtifact id={message.createdArtifact.id} />
          </div>
        </div>
      )}
    </div>
  );
};

const AskAIEmbedsWithSentry = withErrorBoundary(AskAIEmbedsComponent, {
  fallback: ErrorFallback,
});

export default AskAIEmbedsWithSentry;
